import { motion } from "framer-motion";
import Image from "../images/Image";
import LoginBackground from "../Background/LoginBackground";
import { ReactNode } from "react";
import Button from "../buttons/Button";
import ResendOtpButton from "../buttons/ResendOtpButton";
import PopBackground from "../Background/PopBackground";
import MainModel from "./MainModel";
import { useNavigate } from "react-router";
import LanguageChanger from "../LanguageChangerDropdown";
import useTranslation from "../CustomHooks/useTranslation";
import { contents } from "../../pages/auth/AuthContents";

type LoginModelProps = {
  heading: string;
  instruction?: string; // Optional instruction prop
  children?: ReactNode; // Optional children prop
  handleSubmit: () => void; // Define the type for handleSubmit
  buttonName?: string;
  buttonClassName?: string;
  handleResendOtp?: (type: string) => void;
  mainImg: string;
  loginFlow?: boolean;
  loading?: boolean;
  forgotPassword?: boolean;
  testId?: string;
  buttonAlignment?: string;
  handleClose?: () => void;
};

export default function LoginModel({
  heading,
  instruction,
  handleClose,
  children,
  loading,
  buttonName,
  buttonClassName,
  buttonAlignment,
  handleResendOtp,
  handleSubmit,
  testId,
  loginFlow,
  mainImg,
  forgotPassword,
}: LoginModelProps) {
  const navigate = useNavigate();
  const { translate } = useTranslation(contents?.login)
  // Motion animation variants for each element
  const imageVariants = {
    hidden: { opacity: 0 }, // Hidden at first
    visible: { opacity: 1 }, // Fade in
  };

  const headingVariants = {
    hidden: { opacity: 0, y: 10 }, // Starts slightly below
    visible: { opacity: 1, y: 0 }, // Fades in and moves to position
  };

  const instructionVariants = {
    hidden: { opacity: 0 }, // Starts hidden
    visible: { opacity: 1 }, // Fades in smoothly
  };

  const getModel = () => {
    return (
      <MainModel isClose={loginFlow ? false : true} loginFlow={loginFlow} modelWidth="w-[calc(100vw-32px)] max-w-[480px]" handleClose={handleClose}>
        {forgotPassword && (
          <div dir="ltr" className="absolute flex gap-3 left-[32px] top-[32px] cursor-pointer">
            <Image onClick={() => navigate("/")} src="arrow-left" />
            <p className="text-p_primary" onClick={() => navigate("/")}>
              {translate('Back')}
            </p>
          </div>
        )}
        {/* Image with smooth fade-in */}
        <motion.div
          variants={imageVariants}
          initial="hidden"
          animate="visible"
          transition={{ duration: 0.4 }}
          className="flex items-center justify-center"
        >
          <Image src={mainImg} className="" />
        </motion.div>
        {/* Heading with fade and slight upward movement */}
        <motion.h1
          variants={headingVariants}
          initial="hidden"
          animate="visible"
          transition={{
            duration: 0.5,
            ease: "easeInOut",
            delay: 0.2,
          }}
          className="text-primary_text text-heading2 text-center pt-6"
          data-testid="otp-heading"
        >
          {heading}
        </motion.h1>

        {/* Instruction with a smooth fade-in */}
        {instruction && (
          <motion.p
            variants={instructionVariants}
            initial="hidden"
            animate="visible"
            transition={{
              duration: 0.5,
              ease: "easeInOut",
              delay: 0.4,
            }}
            className="text-secondary_text text-body1 text-center p-2 break-all"
          >
            {instruction}
          </motion.p>
        )}

        <div className="flex items-center justify-center w-full">
          <form
            className="w-full"
            onSubmit={(e) => {
              loading ? "" : e.preventDefault();
              handleSubmit();
            }}
          >
            {children}
            <div className={`flex justify-center ${buttonAlignment}`}>
              <Button
                buttonName={buttonName || heading}
                className={`${buttonName === "Send Link" ? 'mt-6' :'mt-2'} w-full ${buttonClassName ? buttonClassName : ""
                  }`}
                loading={loading}
                buttonWidth="w-full"
                buttonType={true}
                testId={testId}
                buttonHight="h-[50px]"
              />
            </div>
          </form>
        </div>
        {handleResendOtp && (
          <motion.div
            variants={{
              hidden: { opacity: 0 }, // Start with opacity 0
              visible: { opacity: 1 }, // Fade in to opacity 1
            }}
            className="flex justify-center items-center"
            initial="hidden"
            animate="visible"
            transition={{
              duration: 0.4,
              ease: "easeInOut", // Smooth transition
              delay: 0.6, // Optional delay to sync with other elements
            }}
          >
            <ResendOtpButton onResend={handleResendOtp} maxResendAttempts={3} />
          </motion.div>
        )}
      </MainModel>
    );
  };

  return (
    <>
      {loginFlow ? (
        <>
          <LoginBackground>
            {getModel()}
            <div className="absolute top-4 right-4 sm:top-[30px] sm:right-[30px]">
              <LanguageChanger image="dropdown-down-arrow" />
            </div>
          </LoginBackground>
        </>
      ) : (
        <PopBackground>{getModel()}</PopBackground>
      )}
    </>
  );
}